class CartDrawerUpsell {
  constructor() {
    this.cachedHTML = null;
    this.currency = 'USD';
    this.products = [];
    this.maxVisible = 8;
    this.collectionHandle = 'shop-all';
    this.observeContainer();
    this.tryInit();
  }

  tryInit() {
    this.container = document.getElementById('cart-drawer-upsell');
    if (!this.container) return;

    if (this.cachedHTML !== null) {
      if (this.container.innerHTML.trim() === '') {
        this.container.innerHTML = this.cachedHTML;
        this.bindButtons();
      }
      return;
    }

    if (!this.container.dataset.loaded) {
      this.container.dataset.loaded = 'true';
      this.loadRecommendations();
    }
  }

  observeContainer() {
    if (window.__cartUpsellObserving) return;
    window.__cartUpsellObserving = true;
    const observer = new MutationObserver(() => this.tryInit());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  async loadRecommendations() {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      if (!cart.items.length) return;

      this.currency = cart.currency; // e.g. "INR", "USD", "GBP"

      const cartProductIds = new Set(
        cart.items.map((item) => String(item.product_id))
      );

      const fetchLimit = this.maxVisible + cartProductIds.size + 10;

      const collRes = await fetch(
        `/collections/${this.collectionHandle}/products.json?limit=${fetchLimit}`
      );
      const data = await collRes.json();

      if (!data.products || !data.products.length) return;

      const filteredProducts = data.products
        .filter((product) => !cartProductIds.has(String(product.id)))
        .filter(
          (product) =>
            product.variants && product.variants.some((v) => v.available)
        )

        .map((product) => {
          const availableVariant = product.variants.find((v) => v.available);
          return {
            ...product,
            url: `/products/${product.handle}`,
            variants: [availableVariant, ...product.variants.filter((v) => v !== availableVariant)],
          };
        })
        .slice(0, this.maxVisible);

      if (!filteredProducts.length) return;

      this.products = filteredProducts;
      this.render(this.products);
    } catch (err) {
      console.error('Upsell load failed', err);
    }
  }

  formatPrice(amount) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(amount));
    } catch (err) {
      return Number(amount).toFixed(2);
    }
  }

  getProductImage(p) {
    if (p.images && p.images.length) return p.images[0].src;
    if (p.variants && p.variants[0] && p.variants[0].featured_image) {
      return p.variants[0].featured_image.src;
    }
    return '';
  }

  render(products) {
    if (!products || !products.length) {
      this.cachedHTML = '';
      this.container.innerHTML = '';
      return;
    }

    // <div class="cart-drawer__upsell-title">Complete your stack</div>
    const html = `
      <div class="cart-drawer__upsell-carousel">
        ${products
          .map(
            (p) => `
          <div class="cart-drawer__upsell-card" data-product-card-id="${p.id}">
            <a href="${p.url}">
              <img src="${this.getProductImage(p)}" alt="${p.title}" loading="lazy">
            </a>
            <a href="${p.url}">
              <div>${p.title}</div>
            </a>
            <div>${this.formatPrice(p.variants[0].price)}</div>
            <button
              class="cart-drawer__upsell-add"
              data-variant-id="${p.variants[0].id}"
              data-product-id="${p.id}"
            >ADD +</button>
          </div>
        `
          )
          .join('')}
      </div>
    `;

    this.cachedHTML = html;
    this.container.innerHTML = html;
    this.bindButtons();
  }

  bindButtons() {
    this.container.querySelectorAll('.cart-drawer__upsell-add').forEach((btn) => {
      btn.addEventListener('click', (e) => this.addToCart(e.currentTarget));
    });
  }

  async addToCart(btn) {
    btn.disabled = true;
    btn.textContent = 'Adding...';

    const productId = btn.dataset.productId;

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: btn.dataset.variantId, quantity: 1 }],
        }),
      });

      const addData = await addRes.json();

      if (!addRes.ok) {
        throw new Error(addData.description || addData.message || 'Add to cart failed');
      }
      this.products = this.products.filter(
        (p) => String(p.id) !== String(productId)
      );
      const card = this.container.querySelector(
        `[data-product-card-id="${productId}"]`
      );

      const finishRemoval = () => this.render(this.products);

      if (card) {
        card.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        card.style.opacity = '0';
        card.style.transform = 'scale(0.92)';
        setTimeout(finishRemoval, 200);
      } else {
        finishRemoval();
      }

      const res = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer-section`);
      const data = await res.json();
      const html = new DOMParser().parseFromString(
        data['cart-drawer-section'],
        'text/html'
      );

      const newInner = html.querySelector('[data-hydration-key="cart-drawer-inner"]');
      const oldInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
        oldInner.className = newInner.className;
      }
    } catch (err) {
      console.error('Add to cart failed', err);
      btn.disabled = false;
      btn.textContent = 'ADD +';
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawerUpsell());
} else {
  new CartDrawerUpsell();
}